/**
 * Purpose: "Related businesses" rail below the detail grid.
 * Inputs: businesses (list), societySlug (optional scope for canonical URLs).
 * Outputs: horizontal scroll rail of compact cards; navigates by slug
 *          (fixes known-issues #12 — previously navigated by numeric id).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import LazyImage from '../../common/LazyImage';
import { SectionHeader, RatingStars } from '../../ui';
import { getListingCoverImageUrl } from '../../../utils/imageFallback';
import { getBusinessUrl } from '../../../utils/urlHelpers';

const RelatedBusinesses = ({ businesses = [], societySlug = null }) => {
  if (!businesses.length) return null;

  return (
    <div className="mt-10">
      <SectionHeader title="Related businesses" />
      <div className="scrollbar-hide -mx-4 overflow-x-auto px-4">
        <div className="flex gap-4 pb-2">
          {businesses.map((related) => (
            <Link
              key={related.id}
              to={getBusinessUrl(related, societySlug)}
              className="w-56 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-700 dark:bg-gray-900 sm:w-64"
            >
              <div className="h-32 w-full bg-gray-100 dark:bg-gray-800">
                <LazyImage
                  src={getListingCoverImageUrl(related)}
                  alt={related.name}
                  className="h-full w-full"
                  showProgress={false}
                />
              </div>
              <div className="p-3">
                <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {related.name}
                </h3>
                <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                  {related.description}
                </p>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="capitalize text-gray-500 dark:text-gray-400">
                    {(related.category || '').replace(/_/g, ' ')}
                  </span>
                  {Number(related.average_rating) > 0 && (
                    <RatingStars value={related.average_rating} size="sm" showValue />
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RelatedBusinesses;
